const HIDDEN_KEYS = {
  caseId: '@lis.case.id',
  caseGuid: '@lis.case.guid',
  caseStatus: '@lis.case.status',
  caseDateCollected: '@lis.case.dateCollected',
  caseDateReceived: '@lis.case.dateReceived',
  caseEntryDateTime: '@lis.case.entryDateTime',
  caseSamplingDateTime: '@lis.case.samplingDateTime',
  caseClinicalDiagnosis: '@lis.case.clinicalDiagnosis',
  patientGuid: '@lis.patient.guid',
  patientFirstName: '@lis.patient.firstName',
  patientLastName: '@lis.patient.lastName',
  patientFullName: '@lis.patient.fullName',
  patientDob: '@lis.patient.dob',
  patientSex: '@lis.patient.sex',
  patientAge: '@lis.patient.age',
  doctorPrimaryGuid: '@lis.doctor.primaryGuid',
  doctorPrimaryName: '@lis.doctor.primaryName',
  doctorReferringGuid: '@lis.doctor.referringGuid',
  doctorReferringName: '@lis.doctor.referringName',
  clinicGuid: '@lis.clinic.guid',
  clinicName: '@lis.clinic.name',
  departmentGuid: '@lis.clinic.departmentGuid',
  departmentName: '@lis.clinic.departmentName',
  specimenGuid: '@lis.specimen.guid',
  specimenId: '@lis.specimen.id',
  specimenType: '@lis.specimen.type',
  specimenDescription: '@lis.specimen.description',
  specimenDetails: '@lis.specimen.details',
  specimenMacroscopicDescription: '@lis.specimen.macroscopicDescription',
  specimenBiopsyInfo: '@lis.specimen.biopsyInfo',
  slideId: '@lis.slide.id',
  slideExternalId: '@lis.slide.externalId',
  slideGuid: '@lis.slide.guid',
  slideBlockId: '@lis.slide.blockId',
  slideBlockGuid: '@lis.slide.blockGuid',
};

const LEGACY_KEYS = {
  caseId: 'caseId',
  caseGuid: 'caseGuid',
  specimenGuid: 'specimenGuid',
  specimenId: 'specimenId',
  slideBlockGuid: 'blockGuid',
  slideBlockId: 'blockId',
  slideGuid: 'slideGuid',
  slideExternalId: 'slideExternalId',
  slideId: 'slideId',
};

function normalizeValue(value) {
  if (value === undefined || value === null) {
    return null;
  }
  const str = String(value).trim();
  return str.length ? str : null;
}

function propertyListToMap(properties) {
  return (properties || []).reduce((acc, prop) => {
    if (!prop || !prop.key) {
      return acc;
    }
    acc[prop.key] = normalizeValue(prop.value);
    return acc;
  }, {});
}

function pickValue(map, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const value = normalizeValue(map[keys[i]]);
    if (value !== null) {
      return value;
    }
  }
  return null;
}

function buildPatientFullName(firstName, lastName) {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
}

function buildSections(values) {
  // GUID fields and the case-status field are intentionally omitted from the UI:
  // they are internal identifiers not useful to clinicians. The values are still
  // computed in buildCaseInformationModel (kept for any programmatic use) but not surfaced.
  return [
    {
      titleKey: 'case-information-section-case',
      fields: [
        {labelKey: 'case-information-field-case-id', value: values.caseId},
        {labelKey: 'case-information-field-collected', value: values.caseDateCollected},
        {labelKey: 'case-information-field-received', value: values.caseDateReceived},
        {labelKey: 'case-information-field-entry', value: values.caseEntryDateTime},
        {labelKey: 'case-information-field-sampling', value: values.caseSamplingDateTime},
        {labelKey: 'case-information-field-clinical-diagnosis', value: values.caseClinicalDiagnosis},
      ],
    },
    {
      titleKey: 'case-information-section-patient',
      fields: [
        {labelKey: 'case-information-field-patient-full-name', value: values.patientFullName},
        {labelKey: 'case-information-field-patient-first-name', value: values.patientFirstName},
        {labelKey: 'case-information-field-patient-last-name', value: values.patientLastName},
        {labelKey: 'case-information-field-patient-dob', value: values.patientDob},
        {labelKey: 'case-information-field-patient-sex', value: values.patientSex},
        {labelKey: 'case-information-field-patient-age', value: values.patientAge},
      ],
    },
    {
      titleKey: 'case-information-section-doctor',
      fields: [
        {labelKey: 'case-information-field-doctor-primary-name', value: values.doctorPrimaryName},
        {labelKey: 'case-information-field-doctor-referring-name', value: values.doctorReferringName},
      ],
    },
    {
      titleKey: 'case-information-section-clinic',
      fields: [
        {labelKey: 'case-information-field-clinic-name', value: values.clinicName},
        {labelKey: 'case-information-field-department-name', value: values.departmentName},
      ],
    },
    {
      titleKey: 'case-information-section-specimen',
      fields: [
        {labelKey: 'case-information-field-biopsy-info', value: values.specimenBiopsyInfo},
        {labelKey: 'case-information-field-specimen-id', value: values.specimenId},
        {labelKey: 'case-information-field-specimen-type', value: values.specimenType},
        {labelKey: 'case-information-field-specimen-description', value: values.specimenDescription},
        {labelKey: 'case-information-field-specimen-details', value: values.specimenDetails},
        {labelKey: 'case-information-field-specimen-macroscopic-description', value: values.specimenMacroscopicDescription},
      ],
    },
    {
      titleKey: 'case-information-section-slide',
      fields: [
        {labelKey: 'case-information-field-slide-id', value: values.slideId},
        {labelKey: 'case-information-field-slide-external-id', value: values.slideExternalId},
        {labelKey: 'case-information-field-block-id', value: values.slideBlockId},
      ],
    },
  ];
}

// Flat field values derived from an image's properties — used both by the detail-modal
// sections and by the images-table case-information columns.
export function buildCaseInformationValues(properties) {
  const map = propertyListToMap(properties);

  const caseId = pickValue(map, [HIDDEN_KEYS.caseId, LEGACY_KEYS.caseId]);
  const caseGuid = pickValue(map, [HIDDEN_KEYS.caseGuid, LEGACY_KEYS.caseGuid]);
  const caseStatus = pickValue(map, [HIDDEN_KEYS.caseStatus]);
  const caseDateCollected = pickValue(map, [HIDDEN_KEYS.caseDateCollected]);
  const caseDateReceived = pickValue(map, [HIDDEN_KEYS.caseDateReceived]);
  const caseEntryDateTime = pickValue(map, [HIDDEN_KEYS.caseEntryDateTime]);
  const caseSamplingDateTime = pickValue(map, [HIDDEN_KEYS.caseSamplingDateTime]);
  const caseClinicalDiagnosis = pickValue(map, [HIDDEN_KEYS.caseClinicalDiagnosis]);

  const patientGuid = pickValue(map, [HIDDEN_KEYS.patientGuid]);
  const patientFirstName = pickValue(map, [HIDDEN_KEYS.patientFirstName]);
  const patientLastName = pickValue(map, [HIDDEN_KEYS.patientLastName]);
  const patientFullName = pickValue(map, [HIDDEN_KEYS.patientFullName]) || buildPatientFullName(patientFirstName, patientLastName);
  const patientDob = pickValue(map, [HIDDEN_KEYS.patientDob]);
  const patientSex = pickValue(map, [HIDDEN_KEYS.patientSex]);
  const patientAge = pickValue(map, [HIDDEN_KEYS.patientAge]);

  const doctorPrimaryGuid = pickValue(map, [HIDDEN_KEYS.doctorPrimaryGuid]);
  const doctorPrimaryName = pickValue(map, [HIDDEN_KEYS.doctorPrimaryName]);
  const doctorReferringGuid = pickValue(map, [HIDDEN_KEYS.doctorReferringGuid]);
  const doctorReferringName = pickValue(map, [HIDDEN_KEYS.doctorReferringName]);

  const clinicGuid = pickValue(map, [HIDDEN_KEYS.clinicGuid]);
  const clinicName = pickValue(map, [HIDDEN_KEYS.clinicName]);
  const departmentGuid = pickValue(map, [HIDDEN_KEYS.departmentGuid]);
  const departmentName = pickValue(map, [HIDDEN_KEYS.departmentName]);

  const specimenGuid = pickValue(map, [HIDDEN_KEYS.specimenGuid, LEGACY_KEYS.specimenGuid]);
  const specimenId = pickValue(map, [HIDDEN_KEYS.specimenId, LEGACY_KEYS.specimenId]);
  const specimenType = pickValue(map, [HIDDEN_KEYS.specimenType]);
  const specimenDescription = pickValue(map, [HIDDEN_KEYS.specimenDescription]);
  const specimenDetails = pickValue(map, [HIDDEN_KEYS.specimenDetails]);
  const specimenMacroscopicDescription = pickValue(map, [HIDDEN_KEYS.specimenMacroscopicDescription]);
  const specimenBiopsyInfo = pickValue(map, [HIDDEN_KEYS.specimenBiopsyInfo]);

  const slideId = pickValue(map, [HIDDEN_KEYS.slideId, LEGACY_KEYS.slideId]);
  const slideExternalId = pickValue(map, [HIDDEN_KEYS.slideExternalId, LEGACY_KEYS.slideExternalId]);
  const slideGuid = pickValue(map, [HIDDEN_KEYS.slideGuid, LEGACY_KEYS.slideGuid]);
  const slideBlockId = pickValue(map, [HIDDEN_KEYS.slideBlockId, LEGACY_KEYS.slideBlockId]);
  const slideBlockGuid = pickValue(map, [HIDDEN_KEYS.slideBlockGuid, LEGACY_KEYS.slideBlockGuid]);

  const values = {
    caseId,
    caseGuid,
    caseStatus,
    caseDateCollected,
    caseDateReceived,
    caseEntryDateTime,
    caseSamplingDateTime,
    caseClinicalDiagnosis,
    patientGuid,
    patientFirstName,
    patientLastName,
    patientFullName,
    patientDob,
    patientSex,
    patientAge,
    doctorPrimaryGuid,
    doctorPrimaryName,
    doctorReferringGuid,
    doctorReferringName,
    clinicGuid,
    clinicName,
    departmentGuid,
    departmentName,
    specimenGuid,
    specimenId,
    specimenType,
    specimenDescription,
    specimenDetails,
    specimenMacroscopicDescription,
    specimenBiopsyInfo,
    slideId,
    slideExternalId,
    slideGuid,
    slideBlockId,
    slideBlockGuid,
  };

  return values;
}

export function buildCaseInformationModel(properties) {
  const map = propertyListToMap(properties);
  const hasHiddenMetadata = Object.keys(map).some(key => key.startsWith('@lis.'));
  const values = buildCaseInformationValues(properties);
  const hasAnyData = Object.values(values).some(value => value !== null);

  return {
    hasAnyData,
    hasHiddenMetadata,
    sections: buildSections(values),
  };
}

// Build the model directly from already-resolved flat values (the image-list `caseInfo` object),
// so the detail modal renders without re-fetching the image's properties.
export function buildCaseInformationModelFromValues(values) {
  const v = values || {};
  const hasAnyData = Object.values(v).some(value => value !== null && value !== undefined && value !== '');
  return {
    hasAnyData,
    hasHiddenMetadata: false,
    sections: buildSections(v),
  };
}
